import superagent from 'superagent';
import superagentJsonp from 'superagent-jsonp';
import { default as SuperAgent } from 'superagent';
import { API, APIConfigParam, APIError, APIRequestParam } from './types';

export let methods = ['get', 'post', 'delete', 'put'] as const;
export type RequestMethod = (typeof methods)[number];

const config: APIConfigParam = {
  successKey: 'success',
  msgKey: 'message',
  dataKey: 'data',
  codeKey: 'code',
  commonErrorMsg: '网络异常',
  abortMsg: '请求取消',
  forbiddenMsg: '抱歉，您没有访问权限',
  unauthorizedMsg: '请先登录',
  resolveBody: false,
  headers: {},
  xhrFlag: true,
  responseTimeout: 5 * 60 * 1e3,
  timeout: 5 * 60 * 1e3,
  beforeSend: () => true,
  resolveAdaptor: data => data,
  rejectAdaptor: error => error,
};

export const api: API = {
  configure: function (conf) {
    Object.assign(config, conf);
  },

  getConf: option => config[option],

  setConf: (option, value) => (config[option] = value),
};

methods.forEach((method: RequestMethod) => {
  /**
   *
   * @param url
   * @param params
   * @param data
   * @param headers
   * @param withCredentials
   * @param notJSON 不以 json 为传输载体
   * @param attach 附件
   * @param attach[].key 附件名
   * @param attach[].value 附件 Blob/Buffer
   * @param field
   * @param beforeSend
   * @param turnoffAdaptor 该次请求关闭适配器
   * @param contentType
   * @param xhrFlag
   * @param timeout
   * @param responseTimeout
   * @param resolveBody
   * @param jsonp
   * @param jsonpCallbackParam
   * @param jsonpCallbackName
   * @param successKey
   * @param msgKey
   * @param dataKey
   * @param codeKey
   * @param progress
   */
  api[method] = <T>({
    url,
    params = {},
    data = {},
    headers = {},
    withCredentials = true,
    notJSON = false,
    attach,
    field,
    beforeSend,
    turnoffAdaptor = false,
    contentType,
    xhrFlag,
    timeout,
    responseTimeout,
    resolveBody = config.resolveBody,
    jsonp = false,
    jsonpCallbackParam = 'callback',
    jsonpCallbackName,

    retry = false,
    beforeRetry = () => {},

    successKey = config.successKey,
    msgKey = config.msgKey,
    dataKey = config.dataKey,
    codeKey = config.codeKey,

    responseType = undefined,

    onProgress = () => {},
  }: APIRequestParam): Promise<T> =>
    new Promise<T>(async (resolve, reject) => {
      retry = retry === false ? 0 : retry;
      xhrFlag = xhrFlag !== undefined ? xhrFlag : config.xhrFlag;

      const request: SuperAgent.SuperAgentRequest = superagent[method](url);

      if (withCredentials) {
        request.withCredentials();
      }

      params._t = new Date().getTime().toString(36);
      request.query(params);

      if (contentType) {
        request.set('Content-Type', contentType);
      } else if (method === 'post' && !attach) {
        if (notJSON) {
          request.set('Content-Type', 'application/x-www-form-urlencoded; charset=UTF-8');
        } else {
          request.set('Content-Type', 'application/json;charset=utf-8');
          data = JSON.stringify(data);
        }
      }

      if (attach) {
        // @ts-ignore
        attach.forEach(item => request.attach(item.key, item.value));
      }

      if (field) {
        // @ts-ignore
        field.forEach(item => request.field(item.key, item.value));
      }

      Object.entries(Object.assign({}, config.headers, headers)).forEach(([key, value]) => {
        if (value) {
          request.set(key, value);
        }
      });

      if (jsonp) {
        request.use(
          superagentJsonp({
            timeout: timeout || config.timeout,
            callbackParam: jsonpCallbackParam,
            callbackName: jsonpCallbackName,
          })
        );
      } else if (xhrFlag) {
        request.set('X-Requested-With', 'XMLHttpRequest');
      }

      request.timeout({
        deadline: timeout || config.timeout,
        response: responseTimeout || config.responseTimeout,
      });

      let error: APIError & {
        response?: SuperAgent.Response;
        http?: Error & SuperAgent.ResponseError;
      };

      try {
        const beforeSendFn = beforeSend || config.beforeSend;

        if (beforeSendFn instanceof Function) {
          if (beforeSendFn(request) === false) {
            request.abort();
            error = {
              msg: config.abortMsg,
              aborted: true,
            };
            return;
          }
        }

        let response: SuperAgent.Response;
        const retryCallback = (err, res) => {
          if (err || res.status >= 400) {
            return beforeRetry(err, res);
          }
          return false;
        };

        if (attach) {
          response = await request
            // .attach()
            .on('progress', onProgress)
            .retry(retry, retryCallback);
        } else if (field) {
          response = await request
            // .field()
            .retry(retry, retryCallback);
        } else {
          response = await request.send(data).responseType(responseType).retry(retry, retryCallback);
        }

        const { status, body } = response;
        
        if (status >= 400) {
          error = {
            msg: config.commonErrorMsg,
            response,
            status: status,
          };
        } else {
          // 直接返回整个响应体
          if (resolveBody) {
            if (turnoffAdaptor) resolve(body);
            else resolve(config.resolveAdaptor(body));

            return;
          }

          const { [successKey]: success, [msgKey]: msg, [dataKey]: data, [codeKey]: code } = body;

          if (jsonp) {
            if (turnoffAdaptor) {
              resolve(body);
            } else {
              resolve(config.resolveAdaptor(body));
            }
          } else {
            if (success) {
              if (turnoffAdaptor) {
                resolve(data);
              } else {
                resolve(config.resolveAdaptor(data));
              }
            } else {
              error = {
                msg: msg || config.commonErrorMsg,
                code,
                response,
                status,
              };
            }
          }
        }
      } catch (e) {
        error = {
          msg: config.commonErrorMsg,
          http: e,
        };
      } finally {
        if (error) {
          if (error.http) {
            const response = error.http.response;

            if (response && error.http.response) {
              error.status = response.status;
              // 未认证
              if (response && response.status === 401) {
                error.msg = (response.body && response.body.msg) || response.text || config.unauthorizedMsg;
                error.response = response;
              } else if (response && response.status === 403) {
                error.msg = (response.body && response.body.msg) || response.text || config.forbiddenMsg;
              } else if (response && response.status === 430) {
                error.msg = (response.body && response.body.msg) || response.text || '请先选择机构';
              }
            }
          }

          if (turnoffAdaptor) {
            reject(error as APIError);
          } else {
            reject(config.rejectAdaptor(error as APIError));
          }
        }
      }
    });
});

export default api;
